import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, Ticket, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

export const CodesManager = () => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        reward: '',
        is_active: true,
    });

    useEffect(() => {
        loadCodes();
    }, []);

    const loadCodes = async () => {
        setLoading(true);
        try {
            const response = await api.get('/codes');
            setCodes(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Failed to load codes:', error);
            toast.error(t('loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setFormData({ code: '', description: '', reward: '', is_active: true });
        setEditMode(false);
        setSelectedCode(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (code) => {
        setFormData({
            code: code.code || '',
            description: code.description || '',
            reward: code.reward || '',
            is_active: code.is_active !== false,
        });
        setEditMode(true);
        setSelectedCode(code);
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.code.trim()) {
            toast.error(t('codesErrorNoCode'));
            return;
        }
        if (!formData.reward.trim()) {
            toast.error(t('codesErrorNoReward'));
            return;
        }

        try {
            const params = { user_id: user?.id, username: user?.username };
            if (editMode && selectedCode) {
                await api.put(`/codes/${selectedCode.id}`, formData, { params });
                toast.success(t('codesUpdated'));
            } else {
                await api.post('/codes', formData, { params });
                toast.success(t('codesCreated'));
            }
            setModalOpen(false);
            loadCodes();
        } catch (error) {
            console.error('Failed to save code:', error);
            toast.error(editMode ? t('updateError') : t('createError'));
        }
    };

    const handleDelete = async () => {
        if (!selectedCode) return;
        try {
            const params = { user_id: user?.id, username: user?.username };
            await api.delete(`/codes/${selectedCode.id}`, { params });
            toast.success(t('codesDeleted'));
            setDeleteModalOpen(false);
            setSelectedCode(null);
            loadCodes();
        } catch (error) {
            console.error('Failed to delete code:', error);
            toast.error(t('deleteError'));
        }
    };

    const handleToggleActive = async (code) => {
        try {
            const params = { user_id: user?.id, username: user?.username };
            await api.put(`/codes/${code.id}`, {
                ...code,
                is_active: !code.is_active,
            }, { params });
            toast.success(code.is_active ? t('codesDeactivated') : t('codesActivated'));
            loadCodes();
        } catch (error) {
            console.error('Failed to toggle code:', error);
            toast.error(t('updateError'));
        }
    };

    const filteredCodes = codes.filter(code =>
        code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.reward?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 w-full sm:max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <Input
                        type="text"
                        placeholder={t('codesSearch')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-11 bg-black/40 border-white/10"
                        data-testid="admin-codes-search"
                    />
                </div>
                <Button
                    onClick={handleOpenCreate}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold shrink-0 w-full sm:w-auto"
                    data-testid="create-code-button"
                >
                    <Plus size={18} className="mr-2" />
                    {t('codesNew')}
                </Button>
            </div>

            {/* Codes List */}
            <div className="rounded-xl border border-white/10 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">{t('loading')}</div>
                ) : filteredCodes.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <Ticket size={32} className="mx-auto mb-2 text-gray-600" />
                        {t('codesNone')}
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {filteredCodes.map((code) => (
                            <div
                                key={code.id}
                                data-testid={`admin-code-${code.id}`}
                                className={cn(
                                    "flex items-center justify-between p-4 bg-card/30 hover:bg-white/5 transition-colors",
                                    !code.is_active && "opacity-50"
                                )}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <code className="font-mono font-bold text-white text-base bg-white/5 px-3 py-1 rounded-lg border border-white/10">
                                            {code.code}
                                        </code>
                                        {!code.is_active && (
                                            <span className="px-2 py-0.5 rounded text-xs bg-red-500/10 text-red-400 border border-red-500/20">
                                                {t('codesInactive')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className="text-sm text-emerald-400 font-medium">{code.reward}</span>
                                        {code.description && (
                                            <>
                                                <span className="text-gray-600">•</span>
                                                <span className="text-sm text-gray-500 truncate">{code.description}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0 ml-4">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleToggleActive(code)}
                                        className={cn(
                                            "text-gray-400",
                                            code.is_active 
                                                ? "hover:text-emerald-400 hover:bg-emerald-500/10" 
                                                : "hover:text-yellow-400 hover:bg-yellow-500/10"
                                        )}
                                        title={code.is_active ? t('codesDeactivate') : t('codesActivate')}
                                    >
                                        {code.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleOpenEdit(code)}
                                        className="text-gray-400 hover:text-amber-400 hover:bg-amber-500/10"
                                        data-testid={`edit-code-${code.id}`}
                                    >
                                        <Pencil size={18} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => { setSelectedCode(code); setDeleteModalOpen(true); }}
                                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                        data-testid={`delete-code-${code.id}`}
                                    >
                                        <Trash2 size={18} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-lg bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Rajdhani'] text-xl flex items-center gap-2">
                            <Ticket className="text-emerald-400" size={20} />
                            {editMode ? t('codesEdit') : t('codesNew')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <Label className="text-gray-400">{t('codesCode')}</Label>
                            <Input
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                placeholder={t('codesCodePlaceholder')}
                                className="bg-black/40 border-white/10 font-mono"
                                data-testid="code-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">{t('codesReward')}</Label>
                            <Input
                                value={formData.reward}
                                onChange={(e) => setFormData({ ...formData, reward: e.target.value })}
                                placeholder={t('codesRewardPlaceholder')}
                                className="bg-black/40 border-white/10"
                                data-testid="reward-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">{t('description')} ({t('codesOptional')})</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder={t('codesDescriptionPlaceholder')}
                                className="bg-black/40 border-white/10"
                                data-testid="description-input"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors",
                                    formData.is_active
                                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                                        : "bg-red-500/10 border-red-500/30 text-red-400"
                                )}
                            >
                                {formData.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                <span className="text-sm">{formData.is_active ? t('codesActiveLabel') : t('codesInactiveLabel')}</span>
                            </button>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button
                                onClick={() => setModalOpen(false)}
                                variant="outline"
                                className="flex-1 border-white/20"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
                                data-testid="save-code-button"
                            >
                                {editMode ? t('save') : t('create')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="max-w-sm bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Rajdhani'] text-xl">
                            {t('confirmDelete')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-gray-400">
                            {t('deleteWarning')}
                        </p>
                        {selectedCode && (
                            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                                <code className="font-mono font-bold text-white">{selectedCode.code}</code>
                                <p className="text-sm text-gray-500 mt-1">{selectedCode.reward}</p>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Button
                                onClick={() => setDeleteModalOpen(false)}
                                variant="outline"
                                className="flex-1 border-white/20"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleDelete}
                                className="flex-1 bg-red-500 hover:bg-red-400 text-white font-semibold"
                                data-testid="confirm-delete-code"
                            >
                                {t('delete')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
